/**
 * useCampaignBucketDeepLink — restores a script/content sub-tab *bucket* from
 * the `?pill` search param so a share link reopens the exact list filter.
 *
 *   tab=scripts&pill=pending  -> ScriptsTab opens on its 'pending' bucket
 *   tab=content&pill=review   -> ContentTab opens on its 'review' bucket
 *
 * NOTE: `?sub` was migrated to `?pill` for scripts/content in Task 4. `?sub`
 * is now reserved exclusively for the insights-drawer (DrawerHeader) and
 * reporting tab. Do not read `?sub` here.
 *
 * Both tabs share the same bucket union ('all' | 'review' | 'pending'), so a
 * single resolver serves both. It is a pure, read-only resolver (URL -> bucket):
 * it never writes the URL back. The caller applies the returned bucket to its
 * own local `activeTab` state, once, on mount (guarded by a ref) so it doesn't
 * fight manual pill clicks.
 *
 * Precedence: a `panel=script-review|content-review` deep link forces the
 * 'review' bucket itself (so the target row loads). That review-forcing WINS —
 * when a review panel deep link is present we return `null` so the two don't
 * fight. `?pill` only steers the bucket on a plain tab share link.
 *
 * `tabKey` ('scripts' | 'content') is matched against the URL `tab` so a stale
 * `?pill` from the other tab can't cross-talk.
 */
import { useSearch } from '@tanstack/react-router';
import type { CampaignSearch } from './search';

// Mirrors ScriptBucket (ScriptsTab) and ContentBucket (ContentTab/types) — both
// are the same union. Kept local to avoid a cross-component import cycle.
export type ReviewListBucket = 'all' | 'review' | 'pending';

const LIST_BUCKETS: readonly ReviewListBucket[] = ['all', 'review', 'pending'];

const isListBucket = (value: string | undefined): value is ReviewListBucket =>
  value !== undefined && (LIST_BUCKETS as readonly string[]).includes(value);

type BucketTabKey = 'scripts' | 'content';

const REVIEW_PANEL_FOR_TAB: Record<BucketTabKey, string> = {
  scripts: 'script-review',
  content: 'content-review',
};

export function useCampaignBucketDeepLink(tabKey: BucketTabKey): ReviewListBucket | null {
  const { tab, pill, panel } = useSearch({ strict: false }) as Partial<CampaignSearch>;

  // Only act on the tab this hook is consumed by — a stale ?pill from another
  // tab must not cross-talk.
  if (tab !== tabKey) return null;

  // A review panel deep link forces the 'review' bucket; let it win.
  if (panel === REVIEW_PANEL_FOR_TAB[tabKey]) return null;

  return isListBucket(pill) ? pill : null;
}
